/**
 * The one initials-or-photo plate `apps/app` draws for a fan: a stored photo
 * when the profile has one, or a plate carrying their initials when it does
 * not (R1's "never a blank circle"). Used by the edit-profile screen's
 * `IdentityBlock` at 96px and by the onboarding photo step at 112px.
 *
 * The plate color is derived from the display name (ISSUE-1111).
 * `avatarPlateStyle` hashes the name onto one of the eight plates in
 * `@sportsmet/theme`'s `avatarPlates`, so the same fan is the same color on
 * every screen. A blank name keeps the fixed `primarySolid` plate. The pair
 * is the same in light and dark, and the letters are white on every plate.
 *
 * Two upload controls draw this plate: the onboarding photo step
 * (`features/onboarding/PhotoStep.tsx`) and the edit-profile photo field
 * (`features/editProfile/EditProfilePhotoField.tsx`). This component itself
 * never uploads; it only renders what its caller resolved.
 */
import type { ReactNode } from "react";

import { avatarPlateStyle } from "./avatarPlateStyle";

export type typeProfileAvatarSize = "md" | "lg";

export type typeProfileAvatarLabelling = "labelled" | "decorative";

export type typeProfileAvatarProps = {
  /** The profile's `displayName`. Drives the initials when there is no photo. */
  displayName: string;
  /** The profile's `photoUrl`. `null` draws the initials plate instead. */
  photoUrl: string | null;
  /** 96px, the edit-profile size, or 112px, the onboarding size. Default "md". */
  size?: typeProfileAvatarSize | undefined;
  /**
   * "decorative" hides the plate from screen readers, for a place that already
   * renders the display name right beside it. "labelled" gives it role="img"
   * and its own accessible name. Default "decorative".
   */
  labelling?: typeProfileAvatarLabelling | undefined;
  /** The caller's own test id, passed straight through to the rendered element. */
  testId?: string | undefined;
};

export const PHOTO_AVATAR_LABEL = "Your photo";

export function resolveDisplayInitials(displayName: string): string {
  const parts = displayName.split(/\s+/).filter((part) => part !== "");
  const first = parts.at(0);
  if (first === undefined) {
    return "?";
  }
  if (parts.length === 1) {
    return first.charAt(0).toUpperCase();
  }
  const last = parts.at(-1);
  if (last === undefined) {
    return "?";
  }
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

export function initialsAvatarLabel(displayName: string): string {
  return `${displayName}, initials avatar`;
}

const PHOTO_CLASS: Record<typeProfileAvatarSize, string> = {
  md: "h-24 w-24 shrink-0 rounded-full object-cover",
  lg: "h-28 w-28 shrink-0 rounded-full object-cover",
};

const INITIALS_CLASS: Record<typeProfileAvatarSize, string> = {
  md: "flex h-24 w-24 shrink-0 items-center justify-center rounded-full text-h2",
  lg: "flex h-28 w-28 shrink-0 items-center justify-center rounded-full text-h1",
};

export const ProfileAvatar = ({
  displayName,
  photoUrl,
  size = "md",
  labelling = "decorative",
  testId,
}: typeProfileAvatarProps): ReactNode => {
  if (photoUrl !== null) {
    return (
      <img
        src={photoUrl}
        alt={labelling === "labelled" ? PHOTO_AVATAR_LABEL : ""}
        data-testid={testId}
        className={PHOTO_CLASS[size]}
      />
    );
  }

  return (
    <span
      aria-hidden={labelling === "decorative" ? "true" : undefined}
      role={labelling === "labelled" ? "img" : undefined}
      aria-label={
        labelling === "labelled" ? initialsAvatarLabel(displayName) : undefined
      }
      data-testid={testId}
      className={INITIALS_CLASS[size]}
      style={avatarPlateStyle(displayName)}
    >
      {resolveDisplayInitials(displayName)}
    </span>
  );
};
